import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import {
  getAvailableSites,
  getCountryStates,
  getEprSiteContext,
  getProtocolList,
  IEprSiteContext,
  IStateApiItem,
  validateAvailableSitesForm,
} from '../helpers';
import {
  IAvailableSiteDetail,
  ICountryLanguageItem,
  IGetAvailableSitesResponse,
  IProtocoItem,
  IStudyItem,
} from '../types';

interface IProps {
  open: boolean;
  onClose: () => void;
  eprOptions: IStudyItem[];
}

type LocationMode = 'zipcode' | 'coordinates';

interface IAvailableSitesValues {
  epr: string;
  language: string;
  protocols: string[];
  stateItemId: string;
  zipcode: string;
  latitude: string;
  longitude: string;
  locationMode: LocationMode;
}

const defaultValues: IAvailableSitesValues = {
  epr: '',
  language: '',
  protocols: [],
  stateItemId: '',
  zipcode: '',
  latitude: '',
  longitude: '',
  locationMode: 'zipcode',
};

const sortStatesByName = (states: IStateApiItem[]): IStateApiItem[] =>
  [...states].sort((a, b) => (a.itemName ?? '').localeCompare(b.itemName ?? ''));

const NO_SITES_AVAILABLE_STATUS = 'no-site-available';

const SITE_TABLE_COLUMNS: { key: keyof IAvailableSiteDetail; label: string }[] = [
  { key: 'siteName', label: 'Site Name' },
  { key: 'siteCode', label: 'Site Code' },
  { key: 'siteID', label: 'Site ID' },
  { key: 'protocol', label: 'Protocol Name' },
  { key: 'siteAddress', label: 'Site Address' },
  { key: 'siteState', label: 'Site State' },
  { key: 'siteCity', label: 'Site City' },
  { key: 'siteZipCode', label: 'Site ZIP Code' },
  { key: 'distance', label: 'Site Distance(Miles)' },
  { key: 'tier', label: 'Site Priority' },
];

const STICKY_LEFT_COLUMN: keyof IAvailableSiteDetail = 'siteName';

const SITE_COLUMN_MIN_WIDTHS: Record<keyof IAvailableSiteDetail, number> = {
  protocol: 130,
  siteID: 120,
  siteCode: 110,
  siteName: 150,
  siteAddress: 200,
  siteState: 110,
  siteCity: 110,
  siteZipCode: 100,
  distance: 100,
  tier: 90,
};

const SITE_TABLE_MIN_WIDTH = Object.values(SITE_COLUMN_MIN_WIDTHS).reduce(
  (total, width) => total + width,
  0
);

const SITE_TABLE_BORDER_COLOR = 'grey.500';
const SITE_TABLE_HEADER_BORDER_COLOR = 'primary.dark';

const siteTableSx = {
  minWidth: SITE_TABLE_MIN_WIDTH,
  borderCollapse: 'collapse' as const,
  '& .MuiTableCell-root': {
    border: '1px solid',
    borderColor: SITE_TABLE_BORDER_COLOR,
  },
  '& .MuiTableHead .MuiTableCell-root': {
    borderColor: SITE_TABLE_HEADER_BORDER_COLOR,
  },
};

type SortDirection = 'asc' | 'desc';

const DEFAULT_SITE_SORT_COLUMN: keyof IAvailableSiteDetail = 'distance';
const DEFAULT_SITE_SORT_DIRECTION: SortDirection = 'asc';

const compareSiteValues = (
  a: IAvailableSiteDetail,
  b: IAvailableSiteDetail,
  key: keyof IAvailableSiteDetail
): number => {
  const aValue = a[key];
  const bValue = b[key];

  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue;
  }

  return String(aValue ?? '').localeCompare(String(bValue ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const formatSiteCellValue = (
  site: IAvailableSiteDetail,
  key: keyof IAvailableSiteDetail
): string => {
  const value = site[key];
  if (key === 'distance' && typeof value === 'number') {
    return value.toFixed(2);
  }
  return String(value ?? '');
};

const stopMenuKeyboardPropagation = (event: React.KeyboardEvent) => {
  event.stopPropagation();
};

const LoadingMessage: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
    <CircularProgress size={20} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const getEprLoadingMessage = (
  isLoadingProtocols: boolean,
  isLoadingSiteContext: boolean,
  isLoadingStates: boolean
): string => {
  if (isLoadingProtocols) {
    return 'Loading languages and protocols...';
  }
  if (isLoadingSiteContext) {
    return 'Loading country...';
  }
  if (isLoadingStates) {
    return 'Loading states...';
  }
  return '';
};

const AvailableSites: React.FC<IProps> = ({ open, onClose, eprOptions }) => {
  const [values, setValues] = useState<IAvailableSitesValues>(defaultValues);
  const [protocolList, setProtocolList] = useState<IProtocoItem[]>([]);
  const [languageOptions, setLanguageOptions] = useState<ICountryLanguageItem[]>([]);
  const [siteContext, setSiteContext] = useState<IEprSiteContext | null>(null);
  const [stateOptions, setStateOptions] = useState<IStateApiItem[]>([]);
  const [isLoadingProtocols, setIsLoadingProtocols] = useState(false);
  const [isLoadingSiteContext, setIsLoadingSiteContext] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [sitesResult, setSitesResult] = useState<IGetAvailableSitesResponse | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [protocolSearch, setProtocolSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof IAvailableSiteDetail | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [columnFilters, setColumnFilters] = useState<
    Partial<Record<keyof IAvailableSiteDetail, string>>
  >({});
  const [openFilterColumn, setOpenFilterColumn] = useState<keyof IAvailableSiteDetail | null>(null);
  const lastSiteSettingFetchRef = useRef('');

  const clearSitesResult = () => {
    setSitesResult(null);
    setFormErrors([]);
  };

  const resetForm = () => {
    setValues(defaultValues);
    setProtocolList([]);
    setSiteContext(null);
    setStateOptions([]);
    setLanguageOptions([]);
    lastSiteSettingFetchRef.current = '';
    clearSitesResult();
    setProtocolSearch('');
    setSortColumn(null);
    setSortDirection('asc');
    setColumnFilters({});
    setOpenFilterColumn(null);
  };

  const onClear = () => {
    resetForm();
  };

  const onEprChange = (epr: string) => {
    lastSiteSettingFetchRef.current = '';
    clearSitesResult();
    setValues((current) => ({
      ...current,
      epr,
      language: '',
      protocols: [],
      stateItemId: '',
    }));
    setProtocolList([]);
    setLanguageOptions([]);
    setSiteContext(null);
    setStateOptions([]);
    setProtocolSearch('');
  };

  const onLanguageChange = (language: string) => {
    lastSiteSettingFetchRef.current = '';
    clearSitesResult();
    setValues((current) => ({
      ...current,
      language,
      protocols: [],
      stateItemId: '',
    }));
    setProtocolList([]);
    setSiteContext(null);
    setStateOptions([]);
    setProtocolSearch('');
  };

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    const populateSiteSetting = async () => {
      const { epr, language } = values;
      if (!epr) {
        lastSiteSettingFetchRef.current = '';
        setLanguageOptions([]);
        setProtocolList([]);
        return;
      }

      const langParam = language || 'en';
      const fetchKey = `${epr}|${langParam}`;
      if (lastSiteSettingFetchRef.current === fetchKey) {
        return;
      }

      try {
        setIsLoadingProtocols(true);
        const siteSettingRes = await getProtocolList(epr, langParam, false);
        lastSiteSettingFetchRef.current = fetchKey;

        const languages = Array.isArray(siteSettingRes.countryLanguageList)
          ? siteSettingRes.countryLanguageList
          : [];
        setLanguageOptions(languages);
        setProtocolList(
          siteSettingRes && Array.isArray(siteSettingRes.protocol) ? siteSettingRes.protocol : []
        );

        if (!language) {
          setValues((current) => ({ ...current, language: langParam }));
        }
      } catch (_error) {
        setLanguageOptions([]);
        setProtocolList([]);
      } finally {
        setIsLoadingProtocols(false);
      }
    };

    populateSiteSetting();
  }, [values.epr, values.language]);

  useEffect(() => {
    let isCancelled = false;

    const loadSiteContextAndStates = async () => {
      const { epr, language } = values;
      if (!epr || !language) {
        setSiteContext(null);
        setStateOptions([]);
        return;
      }

      try {
        setIsLoadingSiteContext(true);
        setIsLoadingStates(true);
        const context = await getEprSiteContext(epr, language);
        if (isCancelled) {
          return;
        }

        setSiteContext(context);
        setValues((current) => ({ ...current, stateItemId: '' }));

        if (context.countryItemId) {
          const states = await getCountryStates(context.countryItemId, language);
          if (!isCancelled) {
            setStateOptions(sortStatesByName(states));
          }
        } else if (!isCancelled) {
          setStateOptions([]);
        }
      } catch (_error) {
        if (!isCancelled) {
          setSiteContext(null);
          setStateOptions([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingSiteContext(false);
          setIsLoadingStates(false);
        }
      }
    };

    loadSiteContextAndStates();

    return () => {
      isCancelled = true;
    };
  }, [values.epr, values.language]);

  const isProtocolDisabled = !values.epr || !values.language || isLoadingProtocols;
  const isStateDisabled =
    isLoadingSiteContext ||
    isLoadingStates ||
    !siteContext?.countryItemId ||
    stateOptions.length === 0;

  const isLoadingEprData =
    Boolean(values.epr) && (isLoadingProtocols || isLoadingSiteContext || isLoadingStates);
  const eprLoadingMessage = getEprLoadingMessage(
    isLoadingProtocols,
    isLoadingSiteContext,
    isLoadingStates
  );

  const selectedStateName =
    stateOptions.find((item) => item.stateItemID === values.stateItemId)?.itemName ?? '';

  const hasNoSitesAvailable =
    sitesResult &&
    sitesResult.patientReferralStatus?.toLowerCase() === NO_SITES_AVAILABLE_STATUS &&
    (!sitesResult.siteDetails || sitesResult.siteDetails.length === 0);

  const filteredProtocolList = useMemo(
    () =>
      protocolList.filter((item) =>
        (item.itemName ?? '').toLowerCase().includes(protocolSearch.trim().toLowerCase())
      ),
    [protocolList, protocolSearch]
  );

  const selectedEprOption = eprOptions.find((item) => item.studyItemId === values.epr) ?? null;

  const selectedState =
    stateOptions.find((item) => item.stateItemID === values.stateItemId) ?? null;

  const filteredAndSortedSiteDetails = useMemo(() => {
    const sites = sitesResult?.siteDetails ? [...sitesResult.siteDetails] : [];

    if (sortColumn) {
      sites.sort((a, b) => {
        const comparison = compareSiteValues(a, b, sortColumn);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return sites.filter((site) =>
      SITE_TABLE_COLUMNS.every((column) => {
        const filterValue = (columnFilters[column.key] ?? '').trim().toLowerCase();
        if (!filterValue) {
          return true;
        }
        return formatSiteCellValue(site, column.key).toLowerCase().includes(filterValue);
      })
    );
  }, [sitesResult, sortColumn, sortDirection, columnFilters]);

  const handleSort = (column: keyof IAvailableSiteDetail) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const selectAllProtocols = () => {
    clearSitesResult();
    setValues((current) => ({
      ...current,
      protocols: protocolList
        .map((item) => item.itemName)
        .filter((itemName): itemName is string => Boolean(itemName)),
    }));
  };

  const deselectAllProtocols = () => {
    clearSitesResult();
    setValues((current) => ({
      ...current,
      protocols: [],
    }));
  };

  const onGetSites = async () => {
    clearSitesResult();
    setSortColumn(DEFAULT_SITE_SORT_COLUMN);
    setSortDirection(DEFAULT_SITE_SORT_DIRECTION);
    setColumnFilters({});
    setOpenFilterColumn(null);

    const validationErrors = validateAvailableSitesForm({
      epr: values.epr,
      language: values.language,
      protocols: values.protocols,
      stateItemId: values.stateItemId,
      siteContext,
      locationMode: values.locationMode,
      zipcode: values.zipcode,
      latitude: values.latitude,
      longitude: values.longitude,
    });

    if (validationErrors.length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    try {
      setIsLoadingSites(true);
      const response = await getAvailableSites({
        eligibleProtocols: values.protocols,
        siteRootPath: values.epr,
        language: values.language,
        patientState: selectedStateName,
        siteSettingsId: siteContext!.siteSettingsItemId,
        protocolSettingsId: siteContext!.dataFolderItemId,
        zipCode: values.locationMode === 'zipcode' ? values.zipcode.trim() : '',
        latitude: values.locationMode === 'coordinates' ? values.latitude.trim() : '',
        longitude: values.locationMode === 'coordinates' ? values.longitude.trim() : '',
      });
      setSitesResult(response);
    } catch (_error) {
      setFormErrors(['Failed to fetch available sites. Please try again.']);
    } finally {
      setIsLoadingSites(false);
    }
  };

  const getSiteCellSx = (columnKey: keyof IAvailableSiteDetail) => ({
    minWidth: SITE_COLUMN_MIN_WIDTHS[columnKey],
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'top' as const,
  });

  const headerCellSx = {
    bgcolor: 'primary.main',
    color: '#ffffff',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    '&.MuiTableCell-stickyHeader': {
      bgcolor: 'primary.main',
      color: '#ffffff',
    },
    '& .MuiTableSortLabel-root': {
      color: '#ffffff !important',
    },
    '& .MuiTableSortLabel-root.Mui-active': {
      color: '#ffffff !important',
    },
    '& .MuiTableSortLabel-root:hover': {
      color: '#ffffff !important',
    },
    '& .MuiTableSortLabel-icon': {
      color: '#ffffff !important',
      opacity: '1 !important',
    },
    '& .MuiIconButton-root': {
      color: '#ffffff !important',
    },
    '& .MuiSvgIcon-root': {
      color: '#ffffff !important',
      fill: '#ffffff !important',
    },
  };

  const updateColumnFilter = (key: keyof IAvailableSiteDetail, value: string) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
  };

  const toggleColumnFilter = (column: keyof IAvailableSiteDetail) => {
    setOpenFilterColumn((current) => (current === column ? null : column));
  };

  const getHeaderCellSx = (columnKey: keyof IAvailableSiteDetail) => ({
    ...headerCellSx,
    minWidth: SITE_COLUMN_MIN_WIDTHS[columnKey],
    position: 'sticky' as const,
    top: 0,
    zIndex: columnKey === STICKY_LEFT_COLUMN ? 5 : 3,
    verticalAlign: 'top' as const,
    ...(columnKey === STICKY_LEFT_COLUMN && {
      left: 0,
      borderRight: '1px solid',
      borderRightColor: SITE_TABLE_HEADER_BORDER_COLOR,
      boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
    }),
  });

  const getBodyCellSx = (columnKey: keyof IAvailableSiteDetail) => ({
    ...getSiteCellSx(columnKey),
    ...(columnKey === STICKY_LEFT_COLUMN && {
      position: 'sticky' as const,
      left: 0,
      zIndex: 1,
      bgcolor: 'background.paper',
      borderRight: '1px solid',
      borderRightColor: SITE_TABLE_BORDER_COLOR,
      boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
    }),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle variant="h5" sx={{ flexShrink: 0, pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" component="span">
            Available Sites to Participants
          </Typography>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 3,
          py: 2.5,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            gap: 1.5,
            width: '100%',
          }}
        >
          <Autocomplete
            id="available-sites-epr-selector"
            size="small"
            options={eprOptions}
            value={selectedEprOption}
            sx={{ width: '100%' }}
            getOptionLabel={(option) => option.studyName ?? ''}
            isOptionEqualToValue={(option, value) => option.studyItemId === value.studyItemId}
            onChange={(_event, newValue) => onEprChange(newValue?.studyItemId ?? '')}
            renderInput={(params) => <TextField {...params} label="EPR" size="small" />}
            noOptionsText="No EPR found"
          />

          <FormControl size="small" sx={{ width: '100%' }}>
            <InputLabel id="available-sites-language-label">Language</InputLabel>
            <Select
              labelId="available-sites-language-label"
              id="available-sites-language-selector"
              value={values.language}
              label="Language"
              disabled={!values.epr || isLoadingProtocols || languageOptions.length === 0}
              onChange={(event) => onLanguageChange(event.target.value)}
            >
              {languageOptions.map((item) => (
                <MenuItem key={item.language ?? item.isoCode} value={item.language ?? ''}>
                  {item.language ?? item.isoCode ?? ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            id="available-sites-country-field"
            label="Country"
            value={siteContext?.countryName ?? ''}
            placeholder={isLoadingSiteContext ? 'Loading country...' : ''}
            InputProps={{ readOnly: true }}
            sx={{ width: '100%' }}
          />

          <FormControl size="small" sx={{ width: '100%' }}>
            <InputLabel id="available-sites-protocol-label">Eligible Protocols</InputLabel>
            <Select
              multiple
              disabled={isProtocolDisabled}
              labelId="available-sites-protocol-label"
              id="available-sites-protocol-selector"
              value={values.protocols}
              label="Eligible Protocols"
              renderValue={(selected) => {
                const items = selected as string[];
                if (items.length <= 2) {
                  return items.join(', ');
                }
                return `${items.length} selected`;
              }}
              onClose={() => setProtocolSearch('')}
              MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 320 } },
              }}
              onChange={(event) => {
                const value = event.target.value;
                clearSitesResult();
                setValues((current) => ({
                  ...current,
                  protocols: typeof value === 'string' ? value.split(',') : value,
                }));
              }}
            >
              <ListSubheader sx={{ bgcolor: 'background.paper', lineHeight: 'unset', py: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search protocols"
                  value={protocolSearch}
                  onChange={(event) => setProtocolSearch(event.target.value)}
                  onKeyDown={stopMenuKeyboardPropagation}
                  onClick={(event) => event.stopPropagation()}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      selectAllProtocols();
                    }}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      deselectAllProtocols();
                    }}
                  >
                    Deselect All
                  </Button>
                </Stack>
              </ListSubheader>
              {filteredProtocolList.length === 0 ? (
                <MenuItem disabled value="">
                  No protocols found
                </MenuItem>
              ) : (
                filteredProtocolList.map((item) => (
                  <MenuItem key={item.itemID} value={item.itemName ?? ''}>
                    <Checkbox checked={values.protocols.includes(item.itemName ?? '')} />
                    {item.itemName ?? ''}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Autocomplete
            id="available-sites-state-selector"
            size="small"
            options={stateOptions}
            value={selectedState}
            disabled={isStateDisabled}
            sx={{ width: '100%' }}
            getOptionLabel={(option) => option.itemName ?? ''}
            isOptionEqualToValue={(option, value) => option.stateItemID === value.stateItemID}
            onChange={(_event, newValue) => {
              if (!newValue) {
                return;
              }
              setValues((current) => ({
                ...current,
                stateItemId: newValue.stateItemID,
              }));
              clearSitesResult();
            }}
            renderInput={(params) => <TextField {...params} label="State" size="small" />}
            noOptionsText="No states found"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
            mt: 1.5,
          }}
        >
          <RadioGroup
            row
            value={values.locationMode}
            onChange={(event) => {
              clearSitesResult();
              setValues((current) => ({
                ...current,
                locationMode: event.target.value as LocationMode,
              }));
            }}
          >
            <FormControlLabel value="zipcode" control={<Radio size="small" />} label="ZIP Code" />
            <FormControlLabel
              value="coordinates"
              control={<Radio size="small" />}
              label="Latitude / Longitude"
            />
          </RadioGroup>

          {values.locationMode === 'zipcode' ? (
            <TextField
              size="small"
              label="ZIP Code"
              value={values.zipcode}
              sx={{ width: 160 }}
              onChange={(event) => {
                clearSitesResult();
                setValues((current) => ({ ...current, zipcode: event.target.value }));
              }}
            />
          ) : (
            <>
              <TextField
                size="small"
                label="Latitude"
                value={values.latitude}
                sx={{ width: 140 }}
                onChange={(event) => {
                  clearSitesResult();
                  setValues((current) => ({ ...current, latitude: event.target.value }));
                }}
              />
              <TextField
                size="small"
                label="Longitude"
                value={values.longitude}
                sx={{ width: 140 }}
                onChange={(event) => {
                  clearSitesResult();
                  setValues((current) => ({ ...current, longitude: event.target.value }));
                }}
              />
            </>
          )}
        </Box>

        {isLoadingEprData && eprLoadingMessage && <LoadingMessage message={eprLoadingMessage} />}

        {formErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formErrors.length === 1 ? (
              formErrors[0]
            ) : (
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {formErrors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {isLoadingSites && (
          <Box sx={{ mt: 3 }}>
            <LoadingMessage message="Fetching available sites..." />
          </Box>
        )}

        {sitesResult && !isLoadingSites && (
          <Box sx={{ mt: 3 }}>
            {hasNoSitesAvailable ? (
              <Alert severity="info">No sites available for the selected criteria.</Alert>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  maxHeight: 'min(50vh, 480px)',
                  overflow: 'auto',
                  border: '2px solid',
                  borderColor: SITE_TABLE_BORDER_COLOR,
                }}
              >
                <Table stickyHeader size="small" sx={siteTableSx}>
                  <TableHead>
                    <TableRow>
                      {SITE_TABLE_COLUMNS.map((column) => (
                        <TableCell
                          key={column.key}
                          sortDirection={sortColumn === column.key ? sortDirection : false}
                          sx={getHeaderCellSx(column.key)}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 0.5,
                            }}
                          >
                            <TableSortLabel
                              active={sortColumn === column.key}
                              direction={sortColumn === column.key ? sortDirection : 'asc'}
                              onClick={() => handleSort(column.key)}
                              sx={{ flex: 1 }}
                            >
                              {column.label}
                            </TableSortLabel>
                            <IconButton
                              size="small"
                              aria-label={`Filter ${column.label}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleColumnFilter(column.key);
                              }}
                              sx={{
                                mt: -0.5,
                                ...(columnFilters[column.key] && {
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                }),
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          {openFilterColumn === column.key && (
                            <TextField
                              size="small"
                              fullWidth
                              autoFocus
                              placeholder={`Filter ${column.label}`}
                              value={columnFilters[column.key] ?? ''}
                              onChange={(event) =>
                                updateColumnFilter(column.key, event.target.value)
                              }
                              onClick={(event) => event.stopPropagation()}
                              onKeyDown={stopMenuKeyboardPropagation}
                              sx={{
                                mt: 1,
                                '& .MuiInputBase-root': {
                                  bgcolor: 'background.paper',
                                  color: 'text.primary',
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAndSortedSiteDetails.length ? (
                      filteredAndSortedSiteDetails.map((site, index) => (
                        <TableRow key={`${site.siteID}-${site.protocol}-${index}`}>
                          {SITE_TABLE_COLUMNS.map((column) => {
                            const displayValue = formatSiteCellValue(site, column.key);
                            return (
                              <TableCell key={column.key} sx={getBodyCellSx(column.key)}>
                                {displayValue}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={SITE_TABLE_COLUMNS.length} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No sites found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0, px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onClear} disabled={isLoadingSites || isLoadingEprData}>
          Clear
        </Button>
        <Button
          onClick={onGetSites}
          variant="contained"
          disabled={isLoadingSites || isLoadingEprData}
        >
          Get Sites
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailableSites;
